import { createReducer, on } from '@ngrx/store';
import { CartActions } from '@web-builder/store/cart/cart.actions';
import type { CartFeatureState } from './cart.selector';
import { type ProductCard, ProductType } from '@common/types/product-card.type';

export const initialCartState: CartFeatureState = {
    cartItems: [],
    numberOfItems: 0,
    totalPrice: 0,
    totalOriginalAmount: 0,
    isAllDigitalProducts: false,
    loaded: false,
};

function updateLS(store: CartFeatureState) {
    window.localStorage.setItem('cartStore', JSON.stringify(store));
}

function getTotalPrice(cartItems: ProductCard[]): number {
    return cartItems.reduce((total, item) => total + +item.price * item.selectedQuantity, 0);
}

function getTotalOriginalPrice(cartItems: ProductCard[]): number {
    return cartItems.reduce((total, item) => total + +(item?.originalPrice ?? item.price) * item.selectedQuantity, 0);
}

function isAllDigitalProducts(cartItems: ProductCard[]): boolean {
    return !cartItems.some((item) => item.type === ProductType.physical);
}

function getCartItemId(cartItem: ProductCard): string {
    if (cartItem.catalogProductId) return cartItem.catalogProductId.toString();
    return cartItem.id;
}

export const cartReducer = createReducer(
    initialCartState,
    on(CartActions.getCartLocalStore, () => {
        const cartStore = JSON.parse(window.localStorage.getItem('cartStore'));
        const updatedStore = cartStore ? cartStore : initialCartState;

        return {
            ...updatedStore,
        };
    }),
    on(CartActions.addItemToCart, (store: CartFeatureState, result) => {
        const existingItem = store.cartItems.find((cartItem) => getCartItemId(cartItem) === getCartItemId(result.cartItem));
        const cartItems = store.cartItems
            .map((cartItem) =>
                getCartItemId(cartItem) !== getCartItemId(result.cartItem)
                    ? cartItem
                    : { ...cartItem, selectedQuantity: cartItem.selectedQuantity + result.cartItem.selectedQuantity },
            )
            .concat(existingItem ? [] : [{ ...result.cartItem }]);
        const updatedStore = {
            ...store,
            cartItems,
            numberOfItems: cartItems.length,
            totalPrice: getTotalPrice(cartItems),
            totalOriginalAmount: getTotalOriginalPrice(cartItems),
            isAllDigitalProducts: isAllDigitalProducts(cartItems),
        };

        updateLS(updatedStore);

        return { ...updatedStore };
    }),
    on(CartActions.removeItemFromCart, (store: CartFeatureState, result) => {
        const cartItems = [...store.cartItems.filter((item) => getCartItemId(item) !== getCartItemId(result.cartItem))];
        const updatedStore = {
            cartItems,
            numberOfItems: cartItems.length,
            totalPrice: getTotalPrice(cartItems),
            totalOriginalAmount: getTotalOriginalPrice(cartItems),
            loaded: true,
            isAllDigitalProducts: isAllDigitalProducts(cartItems),
        };

        updateLS(updatedStore);

        return { ...updatedStore };
    }),
    on(CartActions.setNumberOfItemInCart, (store: CartFeatureState, result) => {
        const cartItems = store.cartItems.map((cartItem) =>
            getCartItemId(cartItem) !== getCartItemId(result.cartItem)
                ? cartItem
                : { ...cartItem, selectedQuantity: result.selectedQuantity, productQuantityError: result.err },
        );

        const updatedStore = {
            ...store,
            cartItems,
            totalPrice: getTotalPrice(cartItems),
            totalOriginalAmount: getTotalOriginalPrice(cartItems),
            isAllDigitalProducts: isAllDigitalProducts(cartItems),
        };

        updateLS(updatedStore);

        return { ...updatedStore };
    }),

    on(CartActions.cartLoaded, (store: CartFeatureState) => {
        const updatedStore = {
            ...store,
            loaded: true,
        };

        return { ...updatedStore };
    }),

    on(CartActions.cartLoading, (store: CartFeatureState) => {
        const updatedStore = {
            ...store,
            loaded: false,
        };

        return { ...updatedStore };
    }),
    on(CartActions.updateCartItemPrice, (store: CartFeatureState, { items }) => {
        const cartItems = store.cartItems.map((cartItem) => {
            const cartItemKey = cartItem.catalogProductId?.toString() ?? cartItem.id;
            const matched = items.find((it) => it.id?.toString() === cartItemKey);
            if (matched) {
                return {
                    ...cartItem,
                    price: matched.amount,
                    originalPrice: matched.originalAmount,
                };
            }
            return cartItem;
        });

        const updatedStore: CartFeatureState = {
            ...store,
            cartItems,
            totalPrice: getTotalPrice(cartItems),
            totalOriginalAmount: getTotalOriginalPrice(cartItems),
        };

        updateLS(updatedStore);

        return { ...updatedStore };
    }),
    on(CartActions.setCartItemOriginalPrice, (store: CartFeatureState) => {
        const cartItems = store.cartItems.map((cartItem) => {
            return {
                ...cartItem,
                price: cartItem?.originalPrice ?? cartItem.price,
                originalPrice: null,
            };
        });

        const updatedStore: CartFeatureState = {
            ...store,
            cartItems,
            totalPrice: getTotalPrice(cartItems),
            totalOriginalAmount: getTotalOriginalPrice(cartItems),
        };

        updateLS(updatedStore);

        return { ...updatedStore };
    }),
);
